import React, { useState, useCallback } from 'react';
import { Popover, Button, Divider, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import UserManagementIcon from '@/assets/icons/user-management.svg?react';
import LogoutIcon from '@/assets/icons/logout.svg?react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import UserMenuHeader from '@/shared/components/ui/atoms/UserMenuHeader/UserMenuHeader';
import ClientEntitySelector from '@/shared/components/ui/molecules/ClientEntitySelector/ClientEntitySelector';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { useChangeBusinessAccount } from '@/features/auth/hooks/useChangeBusinessAccount';
import { useIsFederatedUser } from '@/features/auth/hooks/useIsFederatedUser';
import './UserMenu.css';

export interface UserMenuProps {
  avatarUrl?: string;
  onLogout: () => void;
  canManageUsers: boolean;
}

/**
 * UserMenu
 *
 * Popover menu triggered from avatar click in the header.
 * Composes UserMenuHeader, user management button,
 * ClientEntitySelector (client = interactive, entity = read-only), and logout button.
 *
 * ## Client switch flow
 * 1. User selects a different client from the dropdown
 * 2. Immediately calls POST /auth/change-business-account
 * 3. Backend returns a new JWT token scoped to the new client
 * 4. Token is persisted, user-info cache is invalidated, page navigates to home
 *
 * ## Entity display
 * Entity dropdown is **informative only** (locked/disabled).
 * It shows the platforms associated with the currently active businessAccount.
 * Entity is auto-selected from the first available platform.
 *
 * ## 4 dropdown variants based on permissions
 * - Variant 1 (Super Admin / Viewers): client selector + entity display
 * - Variant 2 (External Admins / Gestor): entity display only
 * - Variant 3 (Comercial / Supervisor): no selectors, user mgmt enabled
 * - Variant 4 (Contestação / Operador): no selectors, user mgmt disabled
 */
export const UserMenu: React.FC<UserMenuProps> = ({
  avatarUrl,
  onLogout,
  canManageUsers,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isFederated } = useIsFederatedUser();
  const [open, setOpen] = useState(false);

  const workspace = useWorkspace();
  const changeBusinessAccount = useChangeBusinessAccount();

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
  }, []);

  const handleClientChange = useCallback(
    (clientId: string) => {
      // Only call change-account if selecting a different client
      if (clientId === workspace.selectedClientId) return;

      setOpen(false);
      changeBusinessAccount.mutate({ businessId: clientId });
    },
    [workspace.selectedClientId, changeBusinessAccount]
  );

  const handleUserManagement = useCallback(() => {
    setOpen(false);
    navigate('/user-management');
  }, [navigate]);

  const handleLogout = useCallback(() => {
    setOpen(false);
    onLogout();
  }, [onLogout]);

  const content = (
    <div className="user-menu" data-testid="user-menu-content">
      <UserMenuHeader
        name={workspace.userName}
        email={workspace.userEmail}
        avatarUrl={avatarUrl}
      />

      {/* RN09/CA08 — a Gestão de acessos não é exibida para usuário de
          cliente federado, independentemente do perfil: quem administra
          usuários é o IdP do cliente. Aqui é remoção, não `disabled`: um
          botão desabilitado ainda comunica que a funcionalidade existe na
          plataforma e faria o usuário procurar o suporte errado. */}
      {canManageUsers && !isFederated && (
        <>
          <Divider className="user-menu__divider" />

          <div className="user-menu__section user-menu__section--settings">
            <div className="user-menu__section-title">CONFIGURAÇÕES</div>
            <Button
              type="text"
              icon={<UserManagementIcon />}
              className="user-menu__action-btn"
              onClick={handleUserManagement}
              disabled={!canManageUsers}
              block
              aria-label={t('User management')}
              data-testid="user-management-btn"
            >
              {t('User management')}
            </Button>
          </div>

          <Divider className="user-menu__divider" />
        </>
      )}

      {(workspace.canSwitchClient || workspace.canSwitchEntity) && (
        <>
          <div className="user-menu__section user-menu__section--clients">
            <div className="user-menu__section-title">CLIENTES</div>
            <ClientEntitySelector
              clients={workspace.clients}
              selectedClientId={workspace.selectedClientId}
              onClientChange={handleClientChange}
              showClientSelector={workspace.canSwitchClient}
            />
          </div>
          <Divider className="user-menu__divider" />
        </>
      )}

      <div className="user-menu__section user-menu__section--logout">
        <Button
          type="text"
          icon={<LogoutIcon />}
          className="user-menu__action-btn user-menu__action-btn--logout"
          onClick={handleLogout}
          block
          aria-label={t('Exit')}
          data-testid="logout-btn"
        >
          {t('Exit')}
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      arrow={false}
      overlayClassName="user-menu__popover"
    >
      <Avatar
        src={avatarUrl}
        icon={!avatarUrl && <UserOutlined />}
        size={32}
        className="user-menu__trigger"
        alt={workspace.userName || 'user avatar'}
        aria-label={t('User menu')}
        data-testid="user-menu-trigger"
      />
    </Popover>
  );
};

export default UserMenu;
